import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Plus, Loader2 } from "lucide-react";
import { compressImage } from "@/lib/image";
import { adminUploadImage } from "@/lib/api";

async function uploadFile(file, onDone) {
  if (file.size > 8 * 1024 * 1024) throw new Error("Image must be under 8MB");
  const dataUri = await compressImage(file);
  const res = await adminUploadImage(dataUri);
  onDone(res.url);
}

export function ImageUpload({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(file, onChange);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:border-gold transition-colors">
        {busy ? <Loader2 className="h-5 w-5 text-gold animate-spin" /> : <Upload className="h-5 w-5 text-slate-400" />}
        <span className="text-sm text-slate-500">{busy ? "Uploading..." : value ? "Replace image" : "Upload image (max 8MB)"}</span>
        <input type="file" accept="image/*" data-testid={testid} className="hidden" onChange={onFile} disabled={busy} />
      </label>
      {value && (
        <div className="relative inline-block mt-3">
          <img src={value} alt="preview" className="h-24 rounded-lg object-cover" />
          <button type="button" onClick={() => onChange("")}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export function ImageListUpload({ value = [], onChange }) {
  const list = Array.isArray(value) ? value : [];
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(file, (url) => onChange([...list, url]));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:border-gold transition-colors">
        {busy ? <Loader2 className="h-5 w-5 text-gold animate-spin" /> : <Plus className="h-5 w-5 text-slate-400" />}
        <span className="text-sm text-slate-500">{busy ? "Uploading..." : "Add image to gallery"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
      </label>
      <div className="flex flex-wrap gap-3 mt-3">
        {list.map((img, i) => (
          <div key={`${img.slice(-32)}-${i}`} className="relative">
            <img src={img} alt={`g${i}`} className="h-20 w-20 rounded-lg object-cover" />
            <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))}
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StringListInput({ value = [], onChange }) {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {list.map((item, i) => (
        <div key={i} className="flex gap-2">
          <input value={item} onChange={(e) => onChange(list.map((v, idx) => (idx === i ? e.target.value : v)))}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-gold text-sm" />
          <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            className="h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, ""])}
        className="inline-flex items-center gap-1.5 text-sm text-navy font-medium">
        <Plus className="h-4 w-4" /> Add item
      </button>
    </div>
  );
}

export function Field({ field, value, onChange }) {
  const base = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none text-sm";
  return (
    <div>
      <label className="block text-sm font-medium text-navy mb-1.5">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
      {field.hint && <p className="text-xs text-slate-400 mb-1.5">{field.hint}</p>}
      {field.type === "textarea" && (
        <textarea rows={field.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${base} resize-none`} data-testid={`field-${field.key}`} />
      )}
      {(field.type === "text" || field.type === "date") && (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${field.key}`} placeholder={field.type === "date" ? "YYYY-MM-DD" : ""} />
      )}
      {field.type === "number" && (
        <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={base} data-testid={`field-${field.key}`} />
      )}
      {field.type === "select" && (
        <select value={value || field.default || field.options[0]} onChange={(e) => onChange(e.target.value)} className={`${base} bg-white`} data-testid={`field-${field.key}`}>
          {field.options.map((o) => <option key={o}>{o}</option>)}
        </select>
      )}
      {field.type === "boolean" && (
        <label className="flex items-center gap-2 cursor-pointer mt-1">
          <input type="checkbox" checked={value ?? field.default ?? false} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 rounded text-gold" data-testid={`field-${field.key}`} />
          <span className="text-sm text-slate-600">Enabled</span>
        </label>
      )}
      {field.type === "image" && <ImageUpload value={value} onChange={onChange} testid={`field-${field.key}`} />}
      {field.type === "imageList" && <ImageListUpload value={value} onChange={onChange} />}
      {field.type === "stringList" && <StringListInput value={value} onChange={onChange} />}
    </div>
  );
}
